import TableC from "@/CommonElements/TableC";
import BtnAsLink from "@/Component/BtnAsLink";
import { useDeletePeriodCodeMutation } from "@/services/basicData";
import { usePeriodCodesQuery } from "@/services/basicData";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import {
  AddSquareIcon,
  DeleteIcon,
  Edit02Icon,
} from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import dayjs from "dayjs";
import { TIME_FORMAT, TIME_FORMAT_12_HOURS } from "@/utils/constants";
import { useSystem } from "@/utils/hooks/useLocation";

export const PeriodCodesList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const { t } = useTranslation();
  const { confirm, ModalComponent } = useConfirm();
  const { getLocalizedText } = useLocalizedText();
  const { currentSystem } = useSystem();
  // ==============
  //   Side Effects
  // ==============
  const { mutate: deletePeriodCode } =
    useDeletePeriodCodeMutation();
  const { data: periodCodesRes, isLoading } = usePeriodCodesQuery({
    params: {
      page: currentPage,
      per_page: 10,
      query: debouncedValue,
    },
  });
  const periodCodes = periodCodesRes?.data?.data ?? [];
  const meta = periodCodesRes?.data?.meta ?? {};

  const columns = [
    {
      name: t("CODE"),
      key: "code",
    },
    {
      name: t("EXAMINATION_PERIOD"),
      key: "examination_period",
      render: (value, rowData) => {
        if (!rowData.examination_period) return "-";
        const period = rowData.examination_period;
        const name = getLocalizedText(period.name_ar, period.name_en);
        const timeFrom = period.time_from
          ? dayjs(period.time_from, TIME_FORMAT).format(TIME_FORMAT_12_HOURS)
          : "";
        const timeTo = period.time_to
          ? dayjs(period.time_to, TIME_FORMAT).format(TIME_FORMAT_12_HOURS)
          : "";
        const timeRange = timeFrom && timeTo ? <span dir="ltr">{`(${timeFrom} - ${timeTo})`}</span> : "";
        return timeRange ? <div>{name}{" "}{timeRange} </div> : name;
      },
    },
    {
      name: t("ASSIGNED_USERS"),
      key: "assigned_users",
      render: (value, rowData) => {
        if (
          !rowData.assigned_users ||
          !Array.isArray(rowData.assigned_users) ||
          rowData.assigned_users.length === 0
        ) {
          return "-";
        }
        const firstUser = getLocalizedText(
          rowData.assigned_users[0].name_ar,
          rowData.assigned_users[0].name_en
        );
        if (rowData.assigned_users.length === 1) return firstUser;
        return `${firstUser} +${rowData.assigned_users.length - 1}`;
      },
    },
    {
      name: t("STATUS"),
      key: "is_active",
      render: (value, rowData) => (
        <Tag color={value ? "success" : "error"}>
          {value ? t("ACTIVE") : t("INACTIVE")}
        </Tag>
      ),
    },
    {
      name: t("ACTIONS"),
      key: "actions",
      filterable: false,
    },
  ];

  // =========
  // functions
  // =========
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteClick = (id) => {
    confirm({
      title: t("CONFIRM_DELETE_PERIOD_CODE_TITLE"),
      content: t("CONFIRM_DELETE_PERIOD_CODE_MESSAGE"),
      okText: t("CONFIRM"),
      cancelText: t("CANCEL"),
      onOk: ({ closeModal }) => {
        deletePeriodCode(
          { id },
          {
            onSuccess: () => {
              closeModal();
            },
          }
        );

        return false;
      },
      onCancel: () => { },
    });
  };

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/${currentSystem}/basic-data/period_codes/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link={`/app/${currentSystem}/basic-data/period_codes/create`}
    >
      {t("CREATE_PERIOD_CODE")}
    </BtnAsLink>
  );

  return (
    <div>
      <TableC
        data={periodCodes}
        // {...restClientSearch}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        columns={columns}
        loading={isLoading}
        actions={getActions}
        renderActions={createButton}
        // emptyState={EmptyState}
        // minVisibleColumns={2}
        skeletonCount={10}
        pagination={{
          current: currentPage,
          total: meta?.total ?? 0,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePageChange,
        }}
      />
      <ModalComponent />
    </div>
  );
};
